import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { autoDetectLanguage } from "@/lib/i18n";
import { registerServiceWorker, isPushSupported } from "@/lib/pushNotifications";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/app/Layout";
import PatientSearch from "@/pages/patient/Search";
import PatientAppointments from "@/pages/patient/Appointments";
import Messages from "@/pages/Messages";
import DMP from "@/pages/DMP";
import Orders from "@/pages/Orders";
import DoctorAgenda from "@/pages/doctor/Agenda";
import DoctorPatients from "@/pages/doctor/Patients";
import DoctorPrescriptions from "@/pages/doctor/Prescriptions";
import VideoRoom from "@/pages/VideoRoom";
import SubscriptionReturn from "@/pages/SubscriptionReturn";
import CardSetupReturn from "@/pages/CardSetupReturn";
import DoctorPayments from "@/pages/doctor/Payments";
import Feed from "@/pages/Feed";
import PublicProfile from "@/pages/PublicProfile";
import Verifications from "@/pages/Verifications";
import Network from "@/pages/Network";
import Jobs from "@/pages/Jobs";
import Courses from "@/pages/Courses";
import Groups from "@/pages/Groups";
import Directory from "@/pages/Directory";
import PublicPro from "@/pages/PublicPro";
import Pricing from "@/pages/Pricing";
import Partenaires from "@/pages/Partenaires";
import PartnerPreRegister from "@/pages/PartnerPreRegister";
import { MentionsLegales, CGU, Privacy } from "@/pages/Legal";
import AdminModeration from "@/pages/AdminModeration";
import AdminLeads from "@/pages/AdminLeads";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function DashboardHome() {
  const { user } = useAuth();
  // New default home for everyone: the social feed
  if (user) return <Navigate to="/dashboard/feed" replace />;
  return <Navigate to="/login" replace />;
}

function HashRouter() {
  const loc = useLocation();
  if (loc.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/tarifs" element={<Pricing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/partenaires" element={<Partenaires />} />
      <Route path="/partners" element={<Partenaires />} />
      <Route path="/partenaires/pre-inscription" element={<PartnerPreRegister />} />
      <Route path="/partenaires/pre-inscription/:type" element={<PartnerPreRegister />} />
      <Route path="/legal/mentions-legales" element={<MentionsLegales />} />
      <Route path="/legal/cgu" element={<CGU />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/pro/:rpps" element={<PublicPro />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<Protected><Layout><DashboardHome /></Layout></Protected>} />
      <Route path="/dashboard/search" element={<Protected><Layout><PatientSearch /></Layout></Protected>} />
      <Route path="/dashboard/appointments" element={<Protected><Layout><PatientAppointments /></Layout></Protected>} />
      <Route path="/dashboard/messages" element={<Protected><Layout><Messages /></Layout></Protected>} />
      <Route path="/dashboard/dmp" element={<Protected><Layout><DMP /></Layout></Protected>} />
      <Route path="/dashboard/orders" element={<Protected><Layout><Orders /></Layout></Protected>} />
      <Route path="/dashboard/agenda" element={<Protected><Layout><DoctorAgenda /></Layout></Protected>} />
      <Route path="/dashboard/encaissements" element={<Protected><Layout><DoctorPayments /></Layout></Protected>} />
      <Route path="/dashboard/patients" element={<Protected><Layout><DoctorPatients /></Layout></Protected>} />
      <Route path="/dashboard/prescriptions" element={<Protected><Layout><DoctorPrescriptions /></Layout></Protected>} />
      <Route path="/dashboard/feed" element={<Protected><Layout><Feed /></Layout></Protected>} />
      <Route path="/dashboard/network" element={<Protected><Layout><Network /></Layout></Protected>} />
      <Route path="/dashboard/verifications" element={<Protected><Layout><Verifications /></Layout></Protected>} />
      <Route path="/dashboard/jobs" element={<Protected><Layout><Jobs /></Layout></Protected>} />
      <Route path="/dashboard/courses" element={<Protected><Layout><Courses /></Layout></Protected>} />
      <Route path="/dashboard/groups" element={<Protected><Layout><Groups /></Layout></Protected>} />
      <Route path="/dashboard/directory" element={<Protected><Layout><Directory /></Layout></Protected>} />
      <Route path="/dashboard/admin/moderation" element={<Protected><Layout><AdminModeration /></Layout></Protected>} />
      <Route path="/dashboard/admin/leads" element={<Protected><Layout><AdminLeads /></Layout></Protected>} />
      <Route path="/u/:userId" element={<Protected><Layout><PublicProfile /></Layout></Protected>} />
      <Route path="/video/:appointmentId" element={<Protected><VideoRoom /></Protected>} />
      <Route path="/abonnement/retour" element={<Protected><SubscriptionReturn ok={true} /></Protected>} />
      <Route path="/abonnement/annule" element={<SubscriptionReturn ok={false} />} />
      <Route path="/rdv/paiement/retour" element={<Protected><CardSetupReturn /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Détection auto de langue à la première visite (façon Facebook).
  // No-op si FEATURE_MULTI_LANG=false (MVP Phase 1 FR uniquement).
  useEffect(() => {
    autoDetectLanguage(api);
    // Enregistre le Service Worker pour les Web Push (silencieux, pas de prompt automatique)
    if (isPushSupported()) registerServiceWorker().catch(() => {});
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <HashRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
